// attendanceApi.ts
import api from '../api/api';
import { Geolocation } from '@capacitor/geolocation';
import { SSIDPlugin } from 'capacitor-ssid-plugin';

export interface AttendanceWorkingHour {
  date: string;
  shift_duration: string;
  In_time: string;
  out_time: string;
  total_working_hours: string;
  total_working_hours_words: string;
  total_hours: number;
}

export interface AttendanceStatus {
  name: string;
  status: string;
  from_date?: string;
  clock_in_time?: string;
  out_time?: string;
  user_id?: string;
}

export interface TodayAttendance {
  name: string;
  date: string;
  clock_in_time?: string;
  additional_in_time?: string;
  additional_out_time?: string;
  end_time?: string;
  total_time?: string;
}

export interface PieData {
  name: string;
  value: number;
}

// Get attendance day-wise working hours for an employee
export const getAttendanceDayWise = async (userId: string, startDate: string, endDate: string) => {
  try {
    const response = await api.get(`/attendance_daywise_working_hours`, {
      params: {
        user_id: userId,
        start_date: startDate,
        end_date: endDate
      }
    });
    return response.data?.data || [];
  } catch (error: any) {
    console.error('Error fetching attendance day-wise:', error);
    // FastAPI uses `detail` for HTTPException bodies (e.g. "Office
    // hours not found"); fall back to `.error` / `.message` for older
    // shapes. Throwing the real backend reason lets the UI surface it.
    const msg =
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error.message;
    throw new Error(msg);
  }
};

// Get attendance summary
export const getAttendanceSummary = async (startDate: string, endDate: string) => {
  try {
    const response = await api.get(`/attendance_summary`, {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching attendance summary:', error);
    throw new Error(error?.response?.data?.error || error.message);
  }
};

// Get all attendance records for a single date (branch-scoped).
// Returns an array of { id, user_id, date, clock_in_time, clock_out_time, status }.
// Used by the dashboard to build a real day-by-day presence trend, since
// /attendance_summary only returns status totals (no per-day breakdown).
export const getAttendanceByDate = async (attendanceDate: string) => {
  try {
    const response = await api.get(`/attendanceAll`, {
      params: { date: attendanceDate },
    });
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
  } catch (error: any) {
    console.error('Error fetching attendance for date:', attendanceDate, error);
    return [];
  }
};

// Get attendance status for a specific date
export const getAttendanceStatus = async (attendanceDate: string) => {
  try {
    const response = await api.get(`/attendance_status`, {
      params: {
        attendance_date: attendanceDate
      }
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching attendance status:', error);
    throw new Error(error?.response?.data?.error || error.message);
  }
};

// ----- Web Clock In / Clock Out -----
// The mobile app uses Capacitor's Geolocation + a native geofence worker
// to auto-clock. The web build needs a manual equivalent: ask the browser
// for the user's coordinates, gate them against the configured office
// geofences (same data the Android `GeofenceManager` reads), and POST to
// the same `/attendance` endpoint the mobile manual clock-out uses.

// Haversine distance in meters. Used to mirror the radius check the
// Android side does with Geofence.setCircularRegion.
const haversineMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Wrap navigator.geolocation in a promise with friendly error messages.
// We use the raw browser API (not @capacitor/geolocation) because this
// function is web-only and the messages should reflect the browser
// permission flow the user actually sees.
const getBrowserCoordinates = (): Promise<{ latitude: number; longitude: number }> =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      reject(new Error("Geolocation isn't supported by this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          reject(
            new Error(
              "Location permission denied. Allow location access in your browser to clock in/out.",
            ),
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          reject(
            new Error(
              "Location unavailable. Try again with a stronger signal or move near a window.",
            ),
          );
        } else if (err.code === err.TIMEOUT) {
          reject(new Error("Location request timed out. Please try again."));
        } else {
          reject(new Error(err.message || "Could not get your location."));
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });

type OfficeGeofence = { lat: number; lon: number; radius: number };

// Same set the AuthContext clears on logout — these are the keys the
// mobile login persists. Anything outside this list is unrelated app
// state (redux, theme, etc.) and shouldn't be in an attendance payload.
const ATTENDANCE_PAYLOAD_KEYS = [
  "accessToken",
  "refreshToken",
  "user",
  "level",
  "employee_id", 
  "branchID",
  "headOfficeID",
  "masterID",
  "fcmtoken",
  "workfromhome",
  "workingHoursStart1",
  "workingHoursEnd1",
  "maxDistance1",
  "officeLon1",
  "officeLat1",
  "workingHoursStart2",
  "workingHoursEnd2",
  "maxDistance2",
  "officeLon2",
  "officeLat2",
  "workingHoursStart3",
  "workingHoursEnd3",
  "maxDistance3",
  "officeLon3",
  "officeLat3",
];

// When the app runs inside Capacitor (mobile WebView), Preferences are
// mirrored to localStorage under the "CapacitorStorage." prefix. On a
// plain browser the same keys are written without the prefix. Read both.
const readStorageValue = (key: string): string | null => {
  const direct = localStorage.getItem(key);
  if (direct !== null) return direct;
  return localStorage.getItem(`CapacitorStorage.${key}`);
};

// Read the up-to-three office geofences the login flow saves. Mirrors
// the Android GeofenceManager (officeLat1/Lon1/maxDistance1 … 3). When
// nothing is configured we return null and skip the client gate — the
// backend revalidates anyway.
const readConfiguredOffices = (): OfficeGeofence[] | null => {
  const offices: OfficeGeofence[] = [];
  for (let i = 1; i <= 3; i++) {
    const lat = parseFloat(readStorageValue(`officeLat${i}`) || "");
    const lon = parseFloat(readStorageValue(`officeLon${i}`) || "");
    const radius = parseFloat(readStorageValue(`maxDistance${i}`) || "");
    if (
      Number.isFinite(lat) &&
      Number.isFinite(lon) &&
      Number.isFinite(radius) &&
      (lat !== 0 || lon !== 0)
    ) {
      offices.push({ lat, lon, radius });
    }
  }
  return offices.length ? offices : null;
};

// Build the same key set mobile's clockOut() does, scoped to attendance
// keys only. Skip the literal string "undefined" that Capacitor writes
// when a value was saved as a JS `undefined`.
const buildAttendanceSnapshot = (): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const key of ATTENDANCE_PAYLOAD_KEYS) {
    const value = readStorageValue(key);
    if (value !== null && value !== "undefined") {
      out[key] = value;
    }
  }
  return out;
};

// Resolve the employee_id the backend requires at body root. Prefer the
// dedicated storage key (set on mobile login); fall back to the decoded
// JWT user object (which is what web login persists).
const resolveEmployeeId = (): string | null => {
  const direct = readStorageValue("employee_id");
  if (direct && direct !== "undefined") return direct;
  const userStr = readStorageValue("user");
  if (!userStr) return null;
  try {
    const u = JSON.parse(userStr);
    return (
      u?.employee_id ||
      u?.user_id ||
      u?.id ||
      u?.sub ||
      null
    );
  } catch {
    return null;
  }
};

export interface WebClockResult {
  ok: boolean;
  latitude: number;
  longitude: number;
  distance?: number; // meters to nearest office, when known
  raw: any;
}

// Fetched once by the Attendance page on mount. The backend resolves it
// from Employee.modeOfWorking / Employee.workfromhome, so this is always
// fresh even when the JWT-derived localStorage values are stale.
export interface MyWorkMode {
  mode_of_working: string | null;
  workfromhome: boolean;
}

export const getMyWorkMode = async (): Promise<MyWorkMode> => {
  try {
    const res = await api.get<MyWorkMode>("/attendance/my-mode");
    return res.data;
  } catch {
    // Non-fatal — fall back to the existing localStorage heuristic.
    return { mode_of_working: null, workfromhome: false };
  }
};

// Web-side Clock In / Clock Out. Throws with a user-friendly message
// when geolocation fails or the user is outside every configured
// geofence (and not flagged work-from-home).
export const sendWebClockEvent = async (
  clockIn: boolean,
  opts: { workfromhome?: boolean } = {},
): Promise<WebClockResult> => {
  // Source of truth for "is this user remote / hybrid?":
  //
  //   1. If the caller passes `opts.workfromhome` explicitly (true OR
  //      false), trust it. The Attendance page sources this from
  //      /attendance/my-mode, which reads Employee.modeOfWorking live —
  //      so it's always fresher than anything in localStorage. Falling
  //      back to localStorage when the explicit value is `false` would
  //      let a stale `true` from a previous remote session override the
  //      current in-office state and silently skip the location prompt.
  //
  //   2. Only when `opts.workfromhome` is `undefined` do we read:
  //        a) Capacitor Preferences "workfromhome" (set on login)
  //        b) The persisted user object's workfromhome / modeOfWorking
  //      as a last-resort hint for callers that don't pass the flag.
  let workFromHome: boolean;

  if (typeof opts.workfromhome === "boolean") {
    workFromHome = opts.workfromhome;
  } else {
    const workFromHomeRaw = (readStorageValue("workfromhome") || "").toLowerCase();
    workFromHome = workFromHomeRaw === "true";

    if (!workFromHome && workFromHomeRaw !== "false") {
      try {
        const userStr = readStorageValue("user");
        if (userStr) {
          const u = JSON.parse(userStr);
          if (u?.workfromhome === true) workFromHome = true;
          const mode = (u?.modeOfWorking || u?.mode_of_working || "").toString().toLowerCase();
          if (mode === "remote" || mode === "hybrid") workFromHome = true;
        }
      } catch {
        /* ignore — fall back to the geofenced flow */
      }
    }
  }

  let latitude = 0;
  let longitude = 0;
  let nearestDistance: number | undefined;

  if (!workFromHome) {
    const coords = await getBrowserCoordinates();
    latitude = coords.latitude;
    longitude = coords.longitude;

    const offices = readConfiguredOffices();
    if (offices) {
      let withinAny = false;
      let nearest = Infinity;
      for (const o of offices) {
        const d = haversineMeters(latitude, longitude, o.lat, o.lon);
        if (d < nearest) nearest = d;
        if (d <= o.radius) {
          withinAny = true;
          break;
        }
      }
      nearestDistance = Math.round(nearest);
      if (!withinAny) {
        throw new Error(
          `You're about ${nearestDistance}m from the nearest office. Move within range to clock ${clockIn ? "in" : "out"}.`,
        );
      }
    }
  }

  const employeeId = resolveEmployeeId();
  if (!employeeId) {
    throw new Error(
      "Couldn't identify your employee record. Please sign out and log in again.",
    );
  }

  // the backend pulls branch / office / config out of it. We mirror
  // that with a scoped snapshot (no unrelated localStorage), promote
  // employee_id to body root (the backend requires it), and serialise
  // the freshly read coordinates as strings (Pydantic schema expects
  // string lat/lng).
  const body = {
    ...buildAttendanceSnapshot(),
    employee_id: employeeId,
    clockIn,
    latitude: String(latitude),
    longitude: String(longitude),
    // Make sure the backend sees the fresh WFH flag — the snapshot we
    // build above reads localStorage, which can be stale right after an
    // admin flips the employee to remote / hybrid. Explicit field wins.
    workfromhome: workFromHome,
  };
  const res = await api.post("/attendance", body);
  return {
    ok: res.status >= 200 && res.status < 300,
    latitude,
    longitude,
    distance: nearestDistance,
    raw: res.data,
  };
};

// Send auto-clock attendance
export const sendAutoClockAttendance = async (employeeId: string) => {
  try {
    // Get current location
    const { coords } = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true
    });

    // Get Wi-Fi SSID if available
    let wifiSSID = null;
    try {
      const ssidResult = await (SSIDPlugin as any).getSSID();
      wifiSSID = ssidResult.ssid;
    } catch (e) {
      console.warn("Wi-Fi SSID not available", e);
    }

    // Send attendance to API
    const response = await api.post("/api/attendance/auto-clock", {
      employee_id: employeeId,
      latitude: coords.latitude,
      longitude: coords.longitude,
      wifi_ssid: wifiSSID
    });

    return response.data;
  } catch (err: any) {
    console.error("Error sending attendance", err);
    throw new Error(err?.response?.data?.error || err.message);
  }
};

// ----- Admin / HR / Manager attendance overrides -----
// Backend enforces role-based scoping: admin / hr head can target any
// employee in the branch, manager only their own reports. UI hides
// these calls for everyone else, but the server is the source of truth.

export const adminMarkPresent = async (payload: {
  user_id?: string;
  employee_id?: string;
  attendance_date?: string; // YYYY-MM-DD
}) => {
  try {
    const body: Record<string, any> = {};
    if (payload.employee_id) body.employee_id = payload.employee_id;
    if (payload.user_id) body.user_id = payload.user_id;
    if (payload.attendance_date) body.attendance_date = payload.attendance_date;
    const res = await api.post("/attendance/admin/mark_present", body);
    return res.data;
  } catch (err: any) {
    console.error("Error marking present:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

// Mirror of adminMarkPresent — inserts an ABSENT row for the given
// employee on the given date.
export const adminMarkAbsent = async (payload: {
  user_id?: string;
  employee_id?: string;
  attendance_date?: string; // YYYY-MM-DD
}) => {
  try {
    const body: Record<string, any> = {};
    if (payload.employee_id) body.employee_id = payload.employee_id;
    if (payload.user_id) body.user_id = payload.user_id;
    if (payload.attendance_date) body.attendance_date = payload.attendance_date;
    const res = await api.post("/attendance/admin/mark_absent", body);
    return res.data;
  } catch (err: any) {
    console.error("Error marking absent:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

export const adminUpdateWorkingHours = async (payload: {
  user_id?: string;
  employee_id?: string;
  attendance_date: string; // YYYY-MM-DD
  clock_in_time?: string;  // "HH:MM"
  clock_out_time?: string; // "HH:MM"
}) => {
  try {
    const body: Record<string, any> = {
      attendance_date: payload.attendance_date,
    };
    if (payload.employee_id) body.employee_id = payload.employee_id;
    if (payload.user_id) body.user_id = payload.user_id;
    if (payload.clock_in_time) body.clock_in_time = payload.clock_in_time;
    if (payload.clock_out_time) body.clock_out_time = payload.clock_out_time;
    const res = await api.post("/attendance/admin/update_working_hours", body);
    return res.data;
  } catch (err: any) {
    console.error("Error updating working hours:", err);
    throw new Error(err?.response?.data?.detail || err.message);
  }
};

// Export attendance to CSV
export const exportAttendanceToCSV = (attendanceData: AttendanceStatus[]) => {
  const headers = ["Name", "In Time", "Out Time", "Status"];
  const rows = attendanceData.map((emp) => [
    emp.name,
    emp.clock_in_time || "-",
    emp.out_time || "-",
    emp.status || "-",
  ]);

  let csvContent = headers.join(",") + "\n" + rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const today = new Date().toISOString().split("T")[0];
  link.setAttribute("download", `attendance_${today}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};